import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-camera-live-view-dialog',
  templateUrl: './camera-live-view-dialog.component.html',
  styleUrls: ['./camera-live-view-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraLiveViewDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCamera>;
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public readonly columns: string[] = ['name', 'location', 'floor', 'owner'];
  public _querySearch: string;

  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly _dialogRef: MatDialogRef<CameraLiveViewDialogComponent>,
    private readonly _camerasAPI: SnCamerasAPI,
  ) {}

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  public onSelected(camera: SnCamera): void {
    this._dialogRef.close(camera);
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }
  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    if (this._querySearch) {
      params.search = this._querySearch;
    }
    return this._camerasAPI
      .getCameraList(this._prepareQueryParams(params))
      .pipe(takeUntil(this._destroy$));
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnCamera[]> {
    return this._camerasAPI.feedCameras(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      status__in: 'Up, Unmanaged',
      ...params,
    };
  }
}
